import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AnalysisPageWrapper,
  AnalysisPageTitle,
  AnalysisPageSubtitle,
  AnalysisSectionTitle,
  AnalysisPageLabel,
  AnalysisPageText,
  UserChargingSection,
  QuickInputsSection,
  QuickInputsHeader,
  QuickInputsUserInputsWrapper,
  QuickInputsSectionTitle,
  QuickInput,
  RecalculateButton,
  RecalcLoadingSection,
  BackButton,
  TopBar,
  ResultsRow,
  StoplightWrapper,
  ResultsSection,
  ResultsHeader,
  ResultsTitle,
  NetTitle,
  ResultsDescription,
  UserChargingSliderWrapper,
  RevCostRow,
  CostLineRow,
  CostAmount,
  CostDivider,
  CostTotal,
  RevCostTitle,
  CaasInfoBox,
} from './AnalysisPage.styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as LeftArrow } from '../../assets/arrow-left-circle.svg';
import { ReactComponent as ChevronDown } from '../../assets/chevron-down.svg';
import { ReactComponent as ChevronUp } from '../../assets/chevron-up.svg';
import PageLayout from '../../components/PageLayout/PageLayout';
import Footer from '../../components/Footer/Footer';
import { useSelector, useDispatch } from 'react-redux';
import {
  populateInputs,
  updateChargingFee,
} from '../../features/inputs/inputs';
import InputSlider from '../../components/InputSlider';
import InputSelect from '../../components/InputSelect';
import InputText from '../../components/InputText';
import Stoplight from '../../components/Stoplight';
import { theme } from '../../styles/styles';
import { useResultPull } from '../../hooks/useResultPull';
import { useSubmitInputs } from '../../hooks/useSubmitInputs';
import { MoonLoader } from 'react-spinners';
import InputButton from '../../components/InputButton';
import ValidationError from '../../components/ValidationError';
import {
  equipmentTypeOptions,
  ownershipModelOptions,
} from '../../features/inputs/inputOptions';

const AnalysisPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputs = useSelector((state) => state.inputs.value);
  const validInputs = useSelector((state) => state.inputs.valid);
  const chargingFee = inputs.chargingFee;
  const chargingType = inputs.chargingType;
  const isNonNetworked = inputs.equipmentType.includes('Non-networked');
  const [showDebug, setShowDebug] = useState(false);
  const [isQuickInputsOpen, setIsQuickInputsOpen] = useState(false);
  const [localChargingPorts, setLocalChargingPorts] = useState(
    inputs.chargingPorts,
  );
  const [localEquipmentType, setLocalEquipmentType] = useState(
    inputs.equipmentType,
  );
  const [localOwnershipModel, setLocalOwnershipModel] = useState(
    inputs.ownershipModel,
  );

  const { submitInputs } = useSubmitInputs();
  const queryClient = useQueryClient();

  const [dataReady, setDataReady] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const hasProcessed = useRef(false);
  const hasLoadedOnce = useRef(false);

  const localValidation = useMemo(() => {
    const messages = [];
    if (
      localChargingPorts === null ||
      localChargingPorts === '' ||
      localChargingPorts === undefined
    ) {
      messages.push('Number of Charging Ports is required');
    } else if (typeof localChargingPorts !== 'number') {
      messages.push('Number of Charging Ports input type is incorrect');
    } else if (
      localChargingPorts < 1 ||
      localChargingPorts + (inputs.existingChargers ?? 0) > 100
    ) {
      messages.push(
        inputs.existingChargers
          ? 'Charging Ports to Install plus Existing Charging Ports cannot exceed 100'
          : 'Number of Charging Ports must be between 1 and 100',
      );
    }
    return { valid: messages.length === 0, messages };
  }, [localChargingPorts]);

  useEffect(() => {
    if (!validInputs) {
      navigate('/input', { state: { scrollToBottom: true } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state on every new navigation to this page
  useEffect(() => {
    hasProcessed.current = false;
    hasLoadedOnce.current = false;
    setDataReady(false);
    setLocalChargingPorts(inputs.chargingPorts);
    setLocalEquipmentType(inputs.equipmentType);
    setLocalOwnershipModel(inputs.ownershipModel);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecalculate = () => {
    if (!localValidation.valid) return;
    const updatedInputs = {
      ...inputs,
      chargingPorts: localChargingPorts,
      equipmentType: localEquipmentType,
      ownershipModel: localOwnershipModel,
    };
    dispatch(populateInputs(updatedInputs));
    setDataReady(false);
    setIsResubmitting(true);
    setIsQuickInputsOpen(false);
    submitInputs(updatedInputs, {
      onSuccess: () => {
        hasProcessed.current = false;
        queryClient.removeQueries({ queryKey: ['results'] });
        setIsResubmitting(false);
      },
      onError: () => {
        setIsResubmitting(false);
        setDataReady(true);
      },
    });
  };

  const { data, isError, error, rateMin, rateMax, activeResult } =
    useResultPull({ enabled: validInputs && !dataReady && !isResubmitting });

  useEffect(() => {
    if (hasProcessed.current) return;

    if (isError) {
      hasProcessed.current = true;
      console.error(error);
      navigate('/input', { state: { popup: true } });
      return;
    }

    if (data && data.results_array) {
      hasProcessed.current = true;
      hasLoadedOnce.current = true;
      let fee;
      if (chargingType === 'Paid') {
        if (chargingFee != null && chargingFee > 0) {
          fee = Math.min(Math.max(chargingFee, rateMin), rateMax);
        } else {
          fee = data.residential_rate_ref;
        }
      } else {
        fee = 0;
      }
      dispatch(updateChargingFee(fee));
      setDataReady(true);
    }
  }, [
    data,
    isError,
    error,
    navigate,
    chargingType,
    chargingFee,
    rateMin,
    rateMax,
    dispatch,
  ]);

  const formatCostAmount = (value) => {
    // The '\u00A0' character holds the space for the placeholder row
    if (value === '') return '\u00A0';
    const abs = Math.abs(value).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    });
    return value < 0 ? `-$${abs}` : `+$${abs}`;
  };

  const formatNet = (value) => {
    const abs = Math.abs(value).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    });
    return value < 0 ? `-$${abs}` : `$${abs}`;
  };

  const renderSliderDisabledMessage = () => {
    if (inputs.ownershipModel === 'Charging-as-a-Service')
      return 'CaaS service provider usually sets the price, dependent on contract terms.';
    if (isNonNetworked)
      return 'Using a non-networked charger renders pricing infeasible: pricing set to zero.';

    return '';
  };

  if (!dataReady && !hasLoadedOnce.current) {
    return (
      <PageLayout page="analysis">
        <AnalysisPageWrapper>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
            }}
          >
            <MoonLoader color={theme.secondaryColor} />
          </div>
        </AnalysisPageWrapper>
      </PageLayout>
    );
  }

  return (
    <PageLayout page="analysis">
      <AnalysisPageWrapper>
        <TopBar>
          <BackButton onClick={() => navigate('/input')}>
            <LeftArrow />
            Adjust Inputs
          </BackButton>
        </TopBar>
        <AnalysisPageTitle>
          Workplace Charging Analysis
          <AnalysisPageSubtitle>
            Review the projected costs, benefits, and return on investment for
            your workplace charging program.
          </AnalysisPageSubtitle>
        </AnalysisPageTitle>
        <AnalysisPageText>
          Based on the workplace and project details you provided, we've
          calculated key financial and operational metrics for your EV charging
          installation. Adjust the charge rate below to explore how pricing
          affects your bottom line, or return to the previous screen to update
          your inputs.
        </AnalysisPageText>
        <AnalysisSectionTitle>
          Adjustments
          <AnalysisPageSubtitle>
            Make adjustments below or return to the previous screen to change
            the initial inputs.
          </AnalysisPageSubtitle>
        </AnalysisSectionTitle>

        <QuickInputsSection>
          <QuickInputsHeader onClick={() => setIsQuickInputsOpen((o) => !o)}>
            <QuickInputsSectionTitle>Input Adjustments</QuickInputsSectionTitle>
            {isQuickInputsOpen ? <ChevronUp /> : <ChevronDown />}
          </QuickInputsHeader>
          {isQuickInputsOpen && (
            <>
              <QuickInputsUserInputsWrapper>
                <QuickInput>
                  <AnalysisPageLabel>EV Charging Ports</AnalysisPageLabel>
                  <InputText
                    type="number"
                    value={localChargingPorts ?? ''}
                    onChange={(val) => setLocalChargingPorts(val)}
                  />
                </QuickInput>
                <QuickInput>
                  <AnalysisPageLabel>Ownership Model</AnalysisPageLabel>
                  <InputSelect
                    options={ownershipModelOptions}
                    value={localOwnershipModel}
                    onChange={(opt) => {
                      setLocalOwnershipModel(opt.value);
                      if (opt.value === 'Charging-as-a-Service') {
                        setLocalEquipmentType('Level 2 - Networked');
                      }
                    }}
                  />
                </QuickInput>
                <QuickInput>
                  <AnalysisPageLabel>Charger Type</AnalysisPageLabel>
                  <InputSelect
                    options={equipmentTypeOptions}
                    value={localEquipmentType}
                    isDisabled={
                      inputs.ownershipModel === 'Charging-as-a-Service' ||
                      localOwnershipModel === 'Charging-as-a-Service'
                    }
                    disabledReason="Locked to Level 2 - Networked when Charging-as-a-Service (CaaS) is selected"
                    onChange={(opt) => setLocalEquipmentType(opt.value)}
                  />
                </QuickInput>
              </QuickInputsUserInputsWrapper>
              <ValidationError messages={localValidation.messages} />
              <RecalculateButton
                disabled={!localValidation.valid}
                onClick={handleRecalculate}
              >
                Recalculate
              </RecalculateButton>
            </>
          )}
        </QuickInputsSection>

        {!dataReady && (
          <RecalcLoadingSection>
            <MoonLoader color={theme.secondaryColor} />
          </RecalcLoadingSection>
        )}

        {dataReady && (
          <>
            <UserChargingSection>
              <AnalysisPageLabel>Per User Electricity Rate</AnalysisPageLabel>
              <AnalysisPageText>
                Use the slider to adjust the per-kWh pricing charged to users
                and see how it impacts the financial outcomes below.
              </AnalysisPageText>
              <UserChargingSliderWrapper
                $sliderHidden={
                  isNonNetworked ||
                  inputs.ownershipModel === 'Charging-as-a-Service'
                }
              >
                <InputSlider
                  span={2}
                  min={rateMin}
                  max={rateMax}
                  precision={2}
                  steps={data?.user_charge_rate_array || []}
                  value={chargingFee}
                  onChange={(e) =>
                    dispatch(updateChargingFee(Number(e.target.value)))
                  }
                  showMinMax
                  prefix="$"
                  suffix=" per kWh"
                  thumbPlainSuffix={true}
                  valueDisplay="tracking-above"
                  filledTrack
                  filledTrackColor="primaryColor"
                  tickMark={data.residential_rate_ref}
                  hideSlider={
                    isNonNetworked ||
                    inputs.ownershipModel === 'Charging-as-a-Service'
                  }
                  disabled={
                    isNonNetworked ||
                    inputs.ownershipModel === 'Charging-as-a-Service'
                  }
                  disabledMessage={renderSliderDisabledMessage()}
                />
              </UserChargingSliderWrapper>
              {inputs.ownershipModel === 'Charging-as-a-Service' && (
                <CaasInfoBox>
                  While CaaS contracts are meant to eliminate upfront costs
                  through long-term fixed monthly payments (assumed 5 years for
                  this tool), depending on the company's policies when offering
                  such contracts, there may still be upfront costs.
                  <br />
                  <br />
                  Additionally, revenue from chargers is likely to be split
                  between the charging company and the property owner, depending
                  on contract terms. Many CaaS contracts are customizable to the
                  property owner's needs, from capping the per-kWh price to
                  shorter or longer contract lengths, but may result in higher
                  monthly payments or upfront costs.
                </CaasInfoBox>
              )}
            </UserChargingSection>

            <AnalysisSectionTitle>
              Program Benefits
              <AnalysisPageSubtitle>
                Stoplight indicators reflect the strength of each outcome based
                on your current inputs and charge rate.
              </AnalysisPageSubtitle>
            </AnalysisSectionTitle>
            {activeResult && (
              <ResultsRow>
                <ResultsSection>
                  <ResultsHeader>
                    <StoplightWrapper>
                      <Stoplight
                        value={activeResult.amenity_stoplight}
                        size={20}
                      />
                    </StoplightWrapper>
                    <ResultsTitle>Employee Amenity Value</ResultsTitle>
                  </ResultsHeader>
                  <ResultsDescription>
                    {activeResult.amenity_rationale}
                  </ResultsDescription>
                </ResultsSection>
                <ResultsSection>
                  <ResultsHeader>
                    <ResultsTitle>
                      Green House Gas (GHG) Emissions Estimate
                    </ResultsTitle>
                  </ResultsHeader>
                  <ResultsDescription>
                    Workplace EV charging could avoid roughly{' '}
                    <strong>
                      {Math.round(
                        activeResult.ghg_avoided_tons,
                      ).toLocaleString()}{' '}
                      tons
                    </strong>{' '}
                    of CO2e emissions (equivalent to{' '}
                    <strong>
                      {activeResult.miles_driven.toLocaleString()} miles
                    </strong>{' '}
                    driven or{' '}
                    <strong>
                      {activeResult.gallons_gas.toLocaleString()} gallons
                    </strong>{' '}
                    of gasoline) using{' '}
                    <strong>
                      {Math.round(
                        activeResult.annual_energy_kwh / 1000,
                      ).toLocaleString()}{' '}
                      MWh
                    </strong>{' '}
                    of energy per year.
                  </ResultsDescription>
                </ResultsSection>
              </ResultsRow>
            )}
            {activeResult && (
              <>
                <AnalysisSectionTitle>
                  Revenue and Costs Estimates
                  <AnalysisPageSubtitle>
                    Costs associated with your project.
                  </AnalysisPageSubtitle>
                </AnalysisSectionTitle>

                {inputs.ownershipModel === 'Direct Ownership' && (
                  <RevCostRow>
                    <ResultsSection>
                      <ResultsHeader>
                        <RevCostTitle>Yearly Costs</RevCostTitle>
                      </ResultsHeader>
                      <CostDivider />

                      {[
                        {
                          label: 'Electricity',
                          value: activeResult.yearly_electrcity_cost,
                        },
                        {
                          label: 'Operations & Maintenance',
                          value: activeResult.yearly_op_maint_cost,
                        },
                        {
                          label: 'Financing',
                          value: activeResult.yearly_financing_cost,
                        },
                        {
                          label: 'Charging Revenue',
                          value: activeResult.yearly_charging_revenue,
                          isRevenue: true,
                        },
                      ].map(({ label, value, isRevenue }) => (
                        <CostLineRow key={label}>
                          <span>{label}</span>
                          <CostAmount $isRevenue={isRevenue}>
                            {formatCostAmount(value)}
                          </CostAmount>
                        </CostLineRow>
                      ))}
                      <CostDivider />
                      <CostTotal>
                        <NetTitle>Net Yearly</NetTitle>
                        <NetTitle>
                          {formatNet(
                            activeResult.yearly_electrcity_cost +
                              activeResult.yearly_op_maint_cost +
                              activeResult.yearly_financing_cost +
                              activeResult.yearly_charging_revenue,
                          )}
                        </NetTitle>
                      </CostTotal>
                    </ResultsSection>

                    <ResultsSection>
                      <ResultsHeader>
                        <RevCostTitle>One-Time Costs</RevCostTitle>
                      </ResultsHeader>
                      <CostDivider />

                      {[
                        {
                          label: 'Equipment',
                          value: activeResult.onetime_equipment,
                        },
                        {
                          label: 'Installation',
                          value: activeResult.onetime_installation,
                        },

                        {
                          label: 'Incentives',
                          value: activeResult.onetime_incentive,
                          isRevenue: true,
                        },
                        {
                          label: '',
                          value: '',
                        },
                      ].map(({ label, value, isRevenue }) => (
                        <CostLineRow key={label}>
                          <span>{label}</span>
                          <CostAmount $isRevenue={isRevenue}>
                            {formatCostAmount(value)}
                          </CostAmount>
                        </CostLineRow>
                      ))}
                      <CostDivider />
                      <CostTotal>
                        <NetTitle>Net One-Time</NetTitle>
                        <NetTitle>
                          {formatNet(
                            activeResult.onetime_equipment +
                              activeResult.onetime_installation +
                              activeResult.onetime_incentive,
                          )}
                        </NetTitle>
                      </CostTotal>
                    </ResultsSection>
                  </RevCostRow>
                )}
                {inputs.ownershipModel === 'Charging-as-a-Service' && (
                  <RevCostRow
                    $singleCol={
                      inputs.ownershipModel === 'Charging-as-a-Service'
                    }
                  >
                    <ResultsSection>
                      <CostTotal>
                        <NetTitle>Monthly Costs</NetTitle>
                        <NetTitle>{formatNet(data.monthly_caas_cost)}</NetTitle>
                      </CostTotal>
                    </ResultsSection>
                  </RevCostRow>
                )}
              </>
            )}
            <Footer />
          </>
        )}
      </AnalysisPageWrapper>
    </PageLayout>
  );
};

AnalysisPage.propTypes = {};

export default AnalysisPage;
